import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

interface UseMemberCalendarAccessOptions {
  workspaceId: string | null;
  memberId: string | null;
  canSeeAllCalendars: boolean;
}

interface UseMemberCalendarAccessResult {
  /** Google calendar_id strings permitidos; null = sem filtro (acesso total). */
  allowedGoogleCalendarIds: string[] | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Resolve agendas visíveis ao membro (Fase 4).
 * Flag off ou gestor/admin → null (sem filtro).
 * Sem linhas na junction → null (compat: acesso total).
 */
export function useMemberCalendarAccess({
  workspaceId,
  memberId,
  canSeeAllCalendars,
}: UseMemberCalendarAccessOptions): UseMemberCalendarAccessResult {
  const [allowedGoogleCalendarIds, setAllowedGoogleCalendarIds] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId || !memberId || !isGcalMultiCalendarEnabled() || canSeeAllCalendars) {
      setAllowedGoogleCalendarIds(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data: syncIds, error: rpcError } = await supabase.rpc(
        "get_member_sync_calendar_ids",
        { p_member_id: memberId },
      );

      if (rpcError) throw rpcError;

      const assigned = (syncIds ?? []) as string[];
      if (assigned.length === 0) {
        setAllowedGoogleCalendarIds(null);
        return;
      }

      const { data: calendars, error: calError } = await supabase
        .from("google_calendar_sync_calendars")
        .select("calendar_id")
        .eq("workspace_id", workspaceId)
        .in("id", assigned)
        .eq("is_enabled", true);

      if (calError) throw calError;

      const ids = (calendars ?? []).map((c) => c.calendar_id).filter(Boolean);
      // Restrição configurada: [] = nenhuma agenda habilitada (não voltar a null = ver todas).
      setAllowedGoogleCalendarIds(ids);
    } catch (error) {
      console.error("Erro ao carregar agendas do membro:", error);
      setAllowedGoogleCalendarIds(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, memberId, canSeeAllCalendars]);

  useEffect(() => {
    void load();
  }, [load]);

  return { allowedGoogleCalendarIds, loading, refresh: load };
}
